import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, ImportWizard } from './ui';
import {
  useC360List, C360Table, ColumnChooser, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/announcements';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const COLUMN_FILTER_META = {
  title: { type: 'text', odataField: 'Title' },
  description: { type: 'text', odataField: 'Description' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
};
const SEARCH_FIELDS = ['Title', 'Description'];

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('tr-TR');
}

function isActiveNow(row) {
  if (row?.isActive === false) return false;
  const now = Date.now();
  const start = row?.effectiveStartDate ? new Date(row.effectiveStartDate).getTime() : null;
  const end = row?.effectiveEndDate ? new Date(row.effectiveEndDate).getTime() : null;
  if (start != null && start > now) return false;
  if (end != null && end < now) return false;
  return true;
}

export function AnnouncementsPage() {
  usePageMeta('Duyurular', 'Duyuru tanımlarını, tarih aralıklarını ve görsellerini yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: görünür kolonların OData alanları + kritik alanlar. effectiveStartDate/
  // effectiveEndDate/createdDate görünür kolon ama COLUMN_FILTER_META'da yok; ayrıca Durum
  // hücresi isActiveNow() ile başlangıç/bitiş tarihlerini okur → always'e eklendi. Id nav.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'EffectiveStartDate', 'EffectiveEndDate', 'CreatedDate'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'EffectiveStartDate',
    defaultSortDir: 'desc',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => navigate('/announcements/new');
  const openDetail = (row) => { if (!row) return; navigate(`/announcements/${row.id}`, { state: { row } }); };
  // #282: kalem (Değiştir) düzenleme moduna açar
  const openEditRow = (row) => { if (!row) return; navigate(`/announcements/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Duyuru silinemedi');
      showToast('success', 'Duyuru silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('title', {
      header: 'Tanım', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('effectiveStartDate', {
      header: 'Başlangıç', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('effectiveEndDate', {
      header: 'Bitiş', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: ({ row }) => (isActiveNow(row.original)
        ? <StatusBadge tone="ok">Aktif</StatusBadge>
        : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.accessor('createdDate', {
      header: 'Oluşturma', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('EffectiveStartDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'title', headerName: 'Tanım' },
        { headerName: 'Başlangıç', valueGetter: ({ row }) => formatDate(row.effectiveStartDate) },
        { headerName: 'Bitiş', valueGetter: ({ row }) => formatDate(row.effectiveEndDate) },
        { headerName: 'Durum', valueGetter: ({ row }) => (isActiveNow(row) ? 'Aktif' : 'Pasif') },
        { field: 'description', headerName: 'Açıklama' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `duyurular_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma ===
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const parseBool = (v) => !['hayir', 'hayır', 'false', '0', 'no', 'pasif'].includes(String(v ?? '').trim().toLowerCase());
  const parseImportDate = (v) => {
    if (!v) return null;
    const date = new Date(v);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };
  const importRowToPayload = (row) => ({
    title: importPick(row, ['Tanım', 'Tanim', 'Başlık', 'Baslik', 'title', 'Title']),
    description: importPick(row, ['Açıklama', 'Aciklama', 'description', 'Description']),
    imageUrl: importPick(row, ['Görsel URL', 'Gorsel URL', 'imageUrl', 'ImageUrl']),
    effectiveStartDate: parseImportDate(importPick(row, ['Başlangıç', 'Baslangic', 'effectiveStartDate', 'EffectiveStartDate'])),
    effectiveEndDate: parseImportDate(importPick(row, ['Bitiş', 'Bitis', 'effectiveEndDate', 'EffectiveEndDate'])),
    isActive: parseBool(importPick(row, ['Durum', 'isActive', 'IsActive'])),
  });
  const importValidateRow = (p) => {
    if (!p.title) return 'Tanım boş';
    if (!p.effectiveStartDate || !p.effectiveEndDate) return 'Tarih aralığı zorunlu';
    if (new Date(p.effectiveEndDate) < new Date(p.effectiveStartDate)) return 'Bitiş başlangıçtan önce';
    return null;
  };
  const importSubmitRow = async (p) => {
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(p) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ 'Tanım': 'Örnek Duyuru', 'Açıklama': 'Açıklama metni', 'Başlangıç': '2025-01-01', 'Bitiş': '2025-12-31', 'Durum': 'Aktif' }], null, 'duyuru_sablon');
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Başlık veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Duyurular"
        subtitle="Duyuru tanımlarını filtreleyin, sıralayın ve yönetin."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Duyuru</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.title}"</strong> duyurusunu silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} duyuru içe aktarıldı`); fetchData(); } }}
        title="Duyuru İçe Aktar"
        helpText="Zorunlu alanlar: Tanım, Başlangıç ve Bitiş tarihi. Durum: Aktif/Pasif (boş ise Aktif)."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
